<script>
import { preventDefault } from "svelte/legacy";
import { fade } from "svelte/transition";
import { toast } from "@zerodevx/svelte-toast";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { eventGroupState, isEventGroupLocked } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import {
    DEFAULT_SYNC_PROPERTIES,
    SYNC_PROPERTY_KEYS,
    syncEventGroups,
} from "@app/lib/syncEventGroups.js";
import * as m from "@app/paraglide/messages.js";
import { hasOriginalText } from "@app/lib/eventGroupTypes.js";

const STORAGE_KEY = "cc-event-group-sync-defaults";

function loadStoredDefaults() {
    try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
        if (!stored || typeof stored !== "object") {
            return { ...DEFAULT_SYNC_PROPERTIES };
        }

        return {
            ...DEFAULT_SYNC_PROPERTIES,
            ...stored,
        };
    } catch {
        return { ...DEFAULT_SYNC_PROPERTIES };
    }
}

function getDefaultFromTo() {
    const current = eventGroupState[projectState.selected];
    if (!current) {
        return { fromId: "", toId: "" };
    }

    if (hasOriginalText(current.type) && current.linkedGroup) {
        const linkedExists = eventGroupState.some(
            (group) => group.id === current.linkedGroup,
        );
        if (linkedExists) {
            return { fromId: current.linkedGroup, toId: current.id };
        }
    }

    const linkedChild = eventGroupState.find(
        (group) => group.linkedGroup === current.id,
    );
    if (linkedChild) {
        return { fromId: current.id, toId: linkedChild.id };
    }

    const other = eventGroupState.find((group) => group.id !== current.id);
    return { fromId: other?.id || "", toId: current.id };
}

const defaults = getDefaultFromTo();
let fromId = $state(defaults.fromId);
let toId = $state(defaults.toId);
let properties = $state(loadStoredDefaults());
let insertMissing = $state(false);
let working = $state(false);
let progress = $state(0);
let statusMsg = $state("");

const propertyOptions = [
    {
        key: "timing",
        label: m.event_group_sync_property_timing(),
        help: m.event_group_sync_property_timing_help(),
    },
    {
        key: "sourceText",
        label: m.event_group_sync_property_source_text(),
        help: m.event_group_sync_property_source_text_help(),
    },
    {
        key: "translationText",
        label: m.event_group_sync_property_translation_text(),
        help: m.event_group_sync_property_translation_text_help(),
    },
    {
        key: "position",
        label: m.event_group_sync_property_position(),
        help: m.event_group_sync_property_position_help(),
    },
    {
        key: "speakers",
        label: m.event_group_sync_property_speakers(),
    },
    {
        key: "style",
        label: m.event_group_sync_property_style(),
        help: m.event_group_sync_property_style_help(),
    },
    {
        key: "notes",
        label: m.event_group_sync_property_notes(),
    },
    {
        key: "reply",
        label: m.event_group_sync_property_reply(),
    },
    {
        key: "tags",
        label: m.event_group_sync_property_tags(),
    },
    {
        key: "recipients",
        label: m.event_group_sync_property_recipients(),
    },
];

function swapDirection() {
    const previousFrom = fromId;
    fromId = toId;
    toId = previousFrom;
}

function runSync() {
    if (working) {
        return;
    }

    try {
        if (!fromId || !toId) {
            throw new Error(m.event_group_sync_missing_group_error());
        }

        if (fromId === toId) {
            throw new Error(m.event_group_sync_same_group_error());
        }

        const fromIndex = eventGroupState.findIndex((group) => group.id === fromId);
        const toIndex = eventGroupState.findIndex((group) => group.id === toId);

        if (fromIndex === -1 || toIndex === -1) {
            throw new Error(m.event_group_sync_missing_group_error());
        }

        if (isEventGroupLocked(toIndex)) {
            throw new Error(m.event_group_locked_toast());
        }

        const hasSelectedProperty = SYNC_PROPERTY_KEYS.some(
            (key) => properties[key],
        );
        if (!hasSelectedProperty) {
            throw new Error(m.event_group_sync_no_properties_error());
        }

        working = true;
        progress = 0;
        statusMsg = m.event_group_sync_status_starting();

        const result = syncEventGroups({
            fromGroup: eventGroupState[fromIndex],
            toGroup: eventGroupState[toIndex],
            properties: { ...properties },
            insertMissing,
            onProgress: ({ current, total }) => {
                progress = total ? Math.round((current / total) * 100) : 100;
                statusMsg = m.event_group_sync_status_progress({
                    current,
                    total,
                });
            },
        });

        eventGroupState[toIndex].events = [...eventGroupState[toIndex].events];

        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...properties }));

        insertHistoryAction({
            name: "sync event group",
            eventGroup: toIndex,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });

        progress = 100;
        statusMsg = m.event_group_sync_status_complete({
            matched: result.matched,
            inserted: result.inserted,
            skipped: result.skipped,
        });

        toast.push(
            m.event_group_sync_success_toast({
                matched: result.matched,
                inserted: result.inserted,
                skipped: result.skipped,
            }),
            { classes: ["toast-success"] },
        );

        hideModal();
    } catch (err) {
        console.log(err, err.message);
        progress = 100;
        statusMsg = err.message;
        toast.push(m.event_group_sync_error_toast({ error: err.message }), {
            classes: ["toast-danger"],
        });
        working = false;
    }
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'eventGroupSync' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="eventGroupSyncTitle"
    aria-describedby="eventGroupSyncDesc"
    aria-modal="true"
    tabindex="-1"
    id="EventGroupSync">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="eventGroupSyncTitle">
                    {m.event_group_sync_title()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.close()}
                    disabled={working}
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                {#if working}
                    <p role="alert">
                        <i class="bi bi-exclamation-diamond-fill"></i>
                        {m.event_group_sync_warning()}
                    </p>
                    <div
                        class="progress"
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100">
                        <div
                            class="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                            style="width: {progress}%;"
                            aria-label={m.event_group_sync_progress_label()}>
                            {progress}%
                        </div>
                    </div>
                    <p class="text-center text-muted small" role="status" aria-live="polite">
                        {statusMsg}
                    </p>
                {:else}
                    <p id="eventGroupSyncDesc" class="text-muted">
                        {m.event_group_sync_description()}
                    </p>
                    <form onsubmit={preventDefault(runSync)}>
                        <div class="row align-items-end">
                            <div class="col-md-5 mb-3">
                                <label class="form-label" for="eventGroupSyncFrom">
                                    {m.event_group_sync_from_label()}
                                </label>
                                <select
                                    class="form-select"
                                    id="eventGroupSyncFrom"
                                    bind:value={fromId}>
                                    <option value="">{m.event_group_sync_select_placeholder()}</option>
                                    {#each eventGroupState as eventGroup (eventGroup.id)}
                                        <option value={eventGroup.id}>{eventGroup.name}</option>
                                    {/each}
                                </select>
                            </div>
                            <div class="col-md-2 mb-3 text-center">
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary"
                                    title={m.event_group_sync_swap_direction()}
                                    aria-label={m.event_group_sync_swap_direction()}
                                    onclick={swapDirection}>
                                    <i class="bi bi-arrow-left-right"></i>
                                </button>
                            </div>
                            <div class="col-md-5 mb-3">
                                <label class="form-label" for="eventGroupSyncTo">
                                    {m.event_group_sync_to_label()}
                                </label>
                                <select
                                    class="form-select"
                                    id="eventGroupSyncTo"
                                    bind:value={toId}>
                                    <option value="">{m.event_group_sync_select_placeholder()}</option>
                                    {#each eventGroupState as eventGroup (eventGroup.id)}
                                        <option value={eventGroup.id}>{eventGroup.name}</option>
                                    {/each}
                                </select>
                            </div>
                        </div>

                        <hr />
                        <p class="lead text-warning m-0 mb-2">
                            {m.event_group_sync_properties_heading()}
                        </p>
                        <div class="row">
                            {#each propertyOptions as option (option.key)}
                                <div class="col-md-6 mb-2">
                                    <div class="form-check">
                                        <input
                                            type="checkbox"
                                            class="form-check-input"
                                            id="eventGroupSync-{option.key}"
                                            bind:checked={properties[option.key]} />
                                        <label
                                            class="form-check-label"
                                            for="eventGroupSync-{option.key}">
                                            {option.label}
                                        </label>
                                    </div>
                                    {#if option.help}
                                        <div class="form-text">{option.help}</div>
                                    {/if}
                                </div>
                            {/each}
                        </div>

                        <hr />
                        <div class="form-check mb-1">
                            <input
                                type="checkbox"
                                class="form-check-input"
                                id="eventGroupSyncInsertMissing"
                                bind:checked={insertMissing} />
                            <label class="form-check-label" for="eventGroupSyncInsertMissing">
                                {m.event_group_sync_insert_missing()}
                            </label>
                        </div>
                        <div class="form-text">
                            {m.event_group_sync_insert_missing_help()}
                        </div>
                    </form>
                {/if}
            </div>
            {#if !working}
                <div class="modal-footer">
                    <button
                        class="btn btn-primary"
                        type="button"
                        id="eventGroupSyncBtn"
                        onclick={runSync}>
                        {m.event_group_sync_button()}
                    </button>
                </div>
            {/if}
        </div>
    </div>
</div>
